import {
  AutoComplete,
  BasicInput,
  Col,
  Container,
  FAB,
  FabSpeedDial,
  FormikCheckBox,
  FormikInputInteger,
  FormikSelect,
  Loading,
  Row,
  useShowNotification
} from '@elotech/components';
import { Informativo, InputFile, Label, TinyMCE } from 'common/components';
import {
  FormikListOperationsProps,
  withFormikListOperations
} from 'common/hocs';
import { useLoading } from 'common/hooks';
import {
  ModeloDadosService,
  PrestarContasService,
  SqlModeloService
} from 'common/service';
import { ModeloDados } from 'common/type';
import {
  CategoriaModeloEnum,
  CategoriaModeloEnumFormatado,
  ClassificacaoOperacaoEnum,
  TipoModeloEnum,
  classificacaoOperacaoFormatada,
  tipoModeloEnumFormatado
} from 'common/type/enum';
import { getBreadcrumbStatus } from 'common/utils';
import FileSaver from 'file-saver';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormGroup } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router';

import { TinyMCEAction } from '../tiny-mce';
import { initialModelo, validationSchemaModelo } from './';
import { SqlsFloatSidebar } from './sqls/SqlsFloatSidebar';

type Props = {
  service: ModeloDadosService;
  options: CategoriaModeloEnum[];
  classificacoes: ClassificacaoOperacaoEnum[];
  prestarContasService: PrestarContasService;
  executarExport: (nome: ModeloDados) => Promise<any>;
  sqlModeloService: SqlModeloService;
} & Partial<FormikListOperationsProps>;

type Params = {
  id: string;
};

const ModeloDadosComponent = (props: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<Params>();

  const {
    options,
    service,
    prestarContasService,
    executarExport,
    sqlModeloService,
    classificacoes = []
  } = props;

  const sortedOptions = options.sort((a, b) => a.localeCompare(b));
  const sortedClassificacoes = classificacoes.sort((a, b) =>
    a.localeCompare(b)
  );

  const formikRef = useRef<Formik<ModeloDados>>(null);
  const showNotification = useShowNotification();
  const [loading, setLoading] = useLoading();
  const [modeloDados, setModeloDados] = useState<ModeloDados>(initialModelo);
  const [arquivo, setArquivo] = useState<File>();

  useEffect(() => {
    if (id && id !== 'new') {
      setLoading(
        service.findById(id).then(({ data }) => {
          const sqlModeloPrestacao = data.sqls?.length
            ? data.sqls[0].sqlModelo
            : undefined;

          setModeloDados({
            ...data,
            sqlModeloPrestacao: sqlModeloPrestacao
          });
        })
      );
    }
  }, [setLoading, id, service]);

  const simam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const simAm = params.get('simam') === 'true';
    return simAm;
  }, [location, modeloDados]);

  const onDownloadArquivoPrestacao = (id: string) => {
    setLoading(
      prestarContasService
        .downloadArquivoPrestacao(id)
        .then(({ arquivo, fileName }) => {
          FileSaver.saveAs(URL.createObjectURL(arquivo), fileName);
        })
    );
  };

  const onAdd = (formProps, fieldName) => newValue => {
    const oldArray = formProps.values[fieldName] || [];

    const newArray = [...oldArray, newValue];

    formProps.setFieldValue(fieldName, newArray);
  };

  const onAddVariavelSqlModelo = (formProps, name, values) => {
    const variavelSqlModelo = values ? [{ sqlModelo: values }] : [];

    formProps.setFieldValue(name, values);
    formProps.setFieldValue('sqls', variavelSqlModelo);
  };

  const onRemove = (formProps, fieldName) => index => {
    const oldArray = formProps.values[fieldName] || [];

    const newArray = [
      ...oldArray.slice(0, index),
      ...oldArray.slice(index + 1)
    ];

    formProps.setFieldValue(fieldName, newArray);
  };

  const onSubmit = (values: ModeloDados) => {
    if (values.categoria !== CategoriaModeloEnum.MOVIMENTO) {
      values.classificacaoOperacao = undefined;
    }

    setLoading(
      service.save(values).then(async ({ data }) => {
        if (arquivo && values.tipo === TipoModeloEnum.OFFICE) {
          await service.saveArquivo(data, arquivo);
        }

        showNotification({
          level: 'success',
          message: 'Modelo de dados salvo com sucesso!'
        });

        history.push(`/modelo-dados/${data}${simam ? '?simam=true' : ''}`);
      })
    );
  };

  const tinyMceActions: TinyMCEAction[] = [
    {
      name: 'exportpdf',
      execute: async () =>
        formikRef.current?.state.values &&
        executarExport(formikRef.current?.state.values!)
    }
  ];

  const onRemoveFile = () => {
    setArquivo(undefined);
    setModeloDados({
      ...modeloDados,
      arquivoNome: undefined,
      arquivoFullPath: undefined
    });
  };

  const onDownloadFile = () => {
    if (!modeloDados.arquivoNome && !arquivo) {
      return showNotification({
        level: 'error',
        message: 'Não foi possível realizar o download do arquivo!'
      });
    }

    if (arquivo) {
      const link = document.createElement('a');
      document.body.appendChild(link);

      link.id = 'blobElement';
      link.style.display = 'none';
      link.href = URL.createObjectURL(new Blob([arquivo]));
      link.download = arquivo.name;
      link.click();
      return;
    }

    setLoading(
      service
        .gerarUrlDownload(
          modeloDados.arquivoFullPath!,
          modeloDados.arquivoNome!
        )
        .then(({ data }) => {
          const link: any = document.createElement('a');
          document.body.appendChild(link);

          link.id = 'blobElement';
          link.style = 'display: none';
          link.href = data.urlAssinada;
          link.download = modeloDados.arquivoNome;
          link.click();

          window.URL.revokeObjectURL(data.urlAssinada);
        })
    );
  };

  const onChangeSimAm2026 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const simAm2026 = e.target.checked;

    formikRef.current?.setFieldValue('simAm2026', simAm2026, true);

    const sqlModeloPrestacaoSimAm2026 =
      modeloDados.sqlModeloPrestacao?.simAm2026;

    if (simAm2026 !== sqlModeloPrestacaoSimAm2026) {
      formikRef.current?.setFieldValue('sqlModeloPrestacao', undefined, true);
    }
  };

  return (
    <Container breadcrumb status={getBreadcrumbStatus(id)}>
      <Loading loading={loading} />
      <Formik
        ref={formikRef}
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={modeloDados}
        validationSchema={validationSchemaModelo(simam)}
      >
        {formProps => {
          const { values, setFieldValue, submitForm } = formProps;

          return (
            <>
              {!simam && (
                <SqlsFloatSidebar
                  onAddSqls={onAdd(formProps, 'sqls')}
                  onRemoveSqls={onRemove(formProps, 'sqls')}
                  sqls={values.sqls?.map(s => s.sqlModelo) || []}
                  sqlModeloService={sqlModeloService}
                />
              )}
              <Row className="mb-xs">
                {simam && (
                  <FormikInputInteger label="Código" name="codigo" size={2} />
                )}
                <BasicInput label="Nome" name="nome" type="text" size={6} />
                <FormikSelect<CategoriaModeloEnum>
                  size={3}
                  name="categoria"
                  label="Categoria"
                  options={sortedOptions}
                  getOptionValue={value => value}
                  getOptionLabel={value => CategoriaModeloEnumFormatado[value]}
                />
                {values.categoria === CategoriaModeloEnum.MOVIMENTO &&
                  sortedClassificacoes.length > 0 && (
                    <FormikSelect<ClassificacaoOperacaoEnum>
                      size={3}
                      name="classificacaoOperacao"
                      label="Classificação da operação"
                      options={sortedClassificacoes}
                      getOptionValue={value => value}
                      getOptionLabel={value =>
                        classificacaoOperacaoFormatada[value]
                      }
                    />
                  )}
                <FormikSelect<TipoModeloEnum>
                  size={3}
                  name="tipo"
                  label="Tipo"
                  options={[TipoModeloEnum.EDITOR, TipoModeloEnum.OFFICE]}
                  getOptionValue={value => value}
                  getOptionLabel={value => tipoModeloEnumFormatado[value]}
                />
              </Row>

              {values.tipo === TipoModeloEnum.EDITOR && (
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>Template</Label>
                      <TinyMCE
                        actions={tinyMceActions}
                        value={values.templateHtml!}
                        onChange={value => setFieldValue('templateHtml', value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}

              {values.tipo === TipoModeloEnum.OFFICE && (
                <Row>
                  <InputFile
                    name="arquivo"
                    fileName={arquivo?.name || values.arquivoNome}
                    label="Arquivo"
                    size={12}
                    onChange={event => setArquivo(event.currentTarget.files[0])}
                    onRemove={onRemoveFile}
                    onDownload={onDownloadFile}
                  />
                </Row>
              )}
              {simam && id === 'new' && (
                <>
                  <Informativo type="INFO" classes="positive" withIcon>
                    É necessário salvar para informar o vínculo do SQL.
                  </Informativo>
                </>
              )}

              {simam && id !== 'new' && (
                <>
                  <Row>
                    <FormikCheckBox
                      size={2}
                      fast
                      className="mb-xs"
                      label="SIM-AM 2026"
                      name="simAm2026"
                      id="simAm2026"
                      onChange={e => onChangeSimAm2026(e)}
                    />
                  </Row>
                  <Row>
                    <BasicInput
                      name="sqlModeloPrestacao"
                      label="Sql"
                      size={12}
                      render={({ form, field }) => (
                        <AutoComplete
                          {...field}
                          getOptionLabel={(sqlModelo: any) => sqlModelo.nome}
                          onSelect={(name, value) => {
                            onAddVariavelSqlModelo(formProps, name, value);
                          }}
                          onSearch={search =>
                            sqlModeloService.findAllAutocomplete(
                              search,
                              true,
                              formikRef.current?.state.values.simAm2026
                            )
                          }
                        />
                      )}
                    />
                  </Row>
                </>
              )}

              <FabSpeedDial icon="ellipsis-v" title="Ações">
                {simam && (
                  <FAB
                    icon="file"
                    data-testid="btnTxt"
                    title="Gerar txt"
                    onClick={() => onDownloadArquivoPrestacao(values.id!)}
                  />
                )}
                <FAB
                  icon="check"
                  title="Salvar"
                  data-testid="btnSave"
                  onClick={submitForm}
                />
              </FabSpeedDial>
            </>
          );
        }}
      </Formik>
    </Container>
  );
};

const withFormikOperations =
  withFormikListOperations<any>(ModeloDadosComponent);

export { withFormikOperations as ModeloDadosComponent };
